import { Fragment, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

import { Menu } from "@ark-ui/react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "@tremor/react";

import FreelerLogo from "@/assets/images/logo-backoffice.svg";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import useCookie from "@/hooks/useCookies";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/providers/PermissionsProvider";
import { REPORT_READ_PERMISSIONS } from "@/services/api/report/permissions";

// eslint-disable-next-line import/no-relative-parent-imports
import * as packageJson from "../../../package.json";

const MainNavbar = () => {
  const location = useLocation();
  const { logout } = useAuth0();
  const [, , deleteCookie] = useCookie("token");
  const { userRole, permissions } = usePermissions();

  const isActive = useCallback(
    (item: { path: string; usePrefix?: boolean }) => {
      const cleanPath = item.path.split("?").at(0) || item.path;
      const currentPath = location.pathname;

      if (item.usePrefix) {
        return currentPath.startsWith(cleanPath);
      }

      return cleanPath === currentPath;
    },
    [location.pathname],
  );

  /**
   * Além da role, alguns itens exigem uma permissão específica — basta uma da lista.
   * Sem isso o item apareceria para toda a role e levaria o usuário a uma tela vazia.
   */
  const hasAnyPermission = useCallback(
    (item: { anyPermissions?: string[] }) =>
      !item.anyPermissions?.length ||
      item.anyPermissions.some((permission) => permissions.includes(permission)),
    [permissions],
  );

  const AVAILABLE_MENU_ITEMS = [
    {
      path: "/operations/dashboard",
      label: "Dashboard",
      roles: [
        "BACKOFFICE",
        "BACKOFFICE_OPS",
        "BACKOFFICE_FINANCIAL",
        "EXTERNAL_BACKOFFICE",
        "EXTERNAL_BACKOFFICE_OPS",
      ],
      usePrefix: true,
    },
    {
      path: "/events?status=SCHEDULED.ONGOING",
      label: "Eventos",
      roles: [
        "BACKOFFICE",
        "BACKOFFICE_OPS",
        "BACKOFFICE_FINANCIAL",
        "EXTERNAL_BACKOFFICE",
        "EXTERNAL_BACKOFFICE_OPS",
      ],
      usePrefix: true,
    },
    {
      // Tela da propria empresa: a API resolve a empresa pelo token.
      //
      // A entrada e `company:read:all`, a permissao de ler a **empresa**: e dela que sai o que a
      // tela e -- cartao, responsavel, plano de tarifas e endereco. Sem ela o item levaria a uma
      // tela que fala de outra coisa (usuarios, especialidades, centros de custo) sob o nome
      // "Minha empresa". A permissao de cada aba segue sendo o corte fino la dentro.
      path: "/my-company",
      label: "Minha empresa",
      roles: ["EXTERNAL_BACKOFFICE"],
      anyPermissions: ["company:read:all"],
      usePrefix: true,
    },
    {
      // A primeira tela de profissionais da empresa (PREC-08c): "Profissionais", abaixo, sempre
      // foi dos perfis internos. Separada porque o que a empresa faz aqui e so o valor do
      // mensalista, e nao gestao de cadastro.
      path: "/corporate-freelers",
      label: "Mensalistas",
      roles: ["EXTERNAL_BACKOFFICE"],
      anyPermissions: ["freeler-price:read:all"],
      usePrefix: true,
    },
    {
      path: "/freelancers?status=ACTIVE.AWAITING_APPROVAL",
      label: "Profissionais",
      roles: ["BACKOFFICE", "BACKOFFICE_OPS", "BACKOFFICE_FINANCIAL"],
      usePrefix: true,
    },
    {
      path: "/financial/payment-orders",
      label: "Pagamentos",
      roles: ["BACKOFFICE", "BACKOFFICE_OPS", "BACKOFFICE_FINANCIAL"],
      usePrefix: true,
    },
    {
      path: "/reports/consumption",
      label: "Relatórios",
      roles: [
        "BACKOFFICE",
        "BACKOFFICE_FINANCIAL",
        "EXTERNAL_BACKOFFICE",
        "EXTERNAL_BACKOFFICE_FINANCIAL",
      ],
      anyPermissions: REPORT_READ_PERMISSIONS,
      usePrefix: true,
    },
    {
      path: "/billing/dashboard",
      label: "Faturas",
      roles: [
        "BACKOFFICE",
        "BACKOFFICE_FINANCIAL",
        "EXTERNAL_BACKOFFICE",
        "EXTERNAL_BACKOFFICE_FINANCIAL",
      ],
      usePrefix: true,
    },
    {
      path: "/kpis/dashboard",
      label: "Indicadores",
      roles: ["BACKOFFICE"],
      usePrefix: true,
    },
    {
      path: "/companies?status=ACTIVE",
      label: "Empresas",
      roles: ["BACKOFFICE"],
      usePrefix: true,
    },
    {
      path: "/settings",
      label: "Configurações",
      roles: ["BACKOFFICE", "BACKOFFICE_OPS"],
      usePrefix: true,
    },
  ];

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    deleteCookie();
  };

  return (
    <nav className="fixed top-0 w-full flex items-center justify-between h-16 px-16 bg-white dark:bg-zinc-800 shadow-md z-50">
      <Menu.Root>
        <Menu.ContextTrigger>
          <img src={FreelerLogo} alt="Freeler Logo" />
        </Menu.ContextTrigger>
        <Menu.Positioner>
          <Menu.Content>
            <Menu.Item value="version">Versão {packageJson.version}</Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
      <div>
        {AVAILABLE_MENU_ITEMS.map((item, index) => {
          return (
            <Fragment key={`${index}-${item.path}`}>
              {item.roles.includes(userRole ?? "") && hasAnyPermission(item) ? (
                <Link to={item.path}>
                  <Button
                    variant="light"
                    className={cn("mr-4", {
                      "text-tremor-brand border-solid border-b-2 border-tremor-brand pb-1":
                        isActive(item),
                      "text-gray-500": !isActive(item),
                    })}
                  >
                    {item.label}
                  </Button>
                </Link>
              ) : null}
            </Fragment>
          );
        })}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="light" className="text-gray-500 hover:text-blue-600">
              Sair
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deseja realmente sair?</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction variant="primary" color="red" onClick={handleLogout}>
                Sair
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </nav>
  );
};

export { MainNavbar };
